// Phase 129 Plan 06 — change-flow hook.
//
// Encapsulates the "Change →" escape hatch state machine for the Quick Start
// page. Per RESEARCH OQ4 the change flow is implemented as a query-param
// state reset (`?change=1`) on the same `/w/:ws/sandbox/quick-start` route —
// NOT a separate route. When `?change=1` is present, the page renders
// Step1Card even if the workspace already has active sessions; on successful
// bind of a NEW session, the demo session is DELETEd and the page exits the
// change flow.
//
// Contract:
//   - `isChangeFlow` — true when the URL has `?change=1`.
//   - `demoSession` — the active session whose origin === 'demo_handoff', or
//     undefined when no such session exists.
//   - `enterChangeFlow()` — pushes `?change=1` (creates a new history entry
//     so the user's back button returns to the consent-banner state).
//   - `exitChangeFlow()` — removes `?change=1` (replace, not push — the
//     param is internal state, not navigation history).
//   - `dismissDemoSession()` — DELETE /sandbox/sessions/<demo session
//     publicId>. Always resolves (never rejects): pre-Query this call ignored
//     the response entirely, and its callers (the quick-start change-flow
//     effect + the E2E test hook) have no error UX for it.

import { useCallback } from 'react';
import { useSearchParams } from 'react-router';
import { useApiMutation } from '@/hooks/use-api-query';

/**
 * Public shape of a sandbox session as far as the change-flow is concerned.
 * Defined locally (not imported from `quick-start.tsx`) to keep this hook
 * standalone-testable. Mirrors the relevant fields on the page-level
 * `SandboxSession` interface — `origin` was added in Phase 129 Plan 05's
 * backend migration; `publicId` is the `ssn_*` external identifier the
 * DELETE endpoint resolves via ResolvePublicIdPipe.
 */
export interface ChangeFlowSession {
  id: string;
  publicId?: string;
  status: string;
  origin?: string;
  // Optional descriptors carried through from the underlying SandboxSession,
  // read by the demo handoff banner (phone shown, type drives WA vs IG copy).
  phone?: string | null;
  type?: 'whatsapp' | 'instagram' | 'facebook';
  senderInstagramUsername?: string | null;
}

export interface UseChangeFlowReturn {
  isChangeFlow: boolean;
  demoSession: ChangeFlowSession | undefined;
  enterChangeFlow: () => void;
  exitChangeFlow: () => void;
  dismissDemoSession: () => Promise<void>;
}

/**
 * Read-only over the active sessions list — caller passes whatever subset
 * makes sense (typically `activeSessions` filtered to `status === 'active'`).
 * The hook does NOT re-filter by status; callers are expected to pre-filter.
 */
export function useChangeFlow(
  activeSessions: ChangeFlowSession[],
): UseChangeFlowReturn {
  const [searchParams, setSearchParams] = useSearchParams();
  // Sessions live on the SSE stream (not query-cached), so there is no key
  // to invalidate here — the caller optimistically drops the row via
  // removeSession and the stream delivers the authoritative delete.
  const { mutateAsync: deleteSession } = useApiMutation<void, { targetId: string }>(
    ({ targetId }) => ({
      path: `/sandbox/sessions/${targetId}`,
      init: { method: 'DELETE' },
    }),
  );

  const isChangeFlow = searchParams.get('change') === '1';
  const demoSession = activeSessions.find((s) => s.origin === 'demo_handoff');

  const enterChangeFlow = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.set('change', '1');
    setSearchParams(next, { replace: false });
  }, [searchParams, setSearchParams]);

  const exitChangeFlow = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('change');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const dismissDemoSession = useCallback(async () => {
    if (!demoSession) return;
    // Backend DELETE accepts the `ssn_*` publicId via ResolvePublicIdPipe
    // (sandbox.controller.ts). Prefer `publicId` when present (test
    // fixtures distinguish raw-id from publicId); fall back to `id`,
    // which IS the publicId in production responses (sandbox.service.ts
    // strips the raw UUID in toPublicSession before the wire).
    const targetId = demoSession.publicId ?? demoSession.id;
    try {
      await deleteSession({ targetId });
    } catch {
      // Preserve pre-Query semantics: this DELETE never surfaced errors to
      // the caller (fetchWithAuth resolves non-2xx); the change flow
      // proceeds either way and the SSE stream remains authoritative.
    }
  }, [demoSession, deleteSession]);

  return {
    isChangeFlow,
    demoSession,
    enterChangeFlow,
    exitChangeFlow,
    dismissDemoSession,
  };
}
